/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { apiClient } from '../api/client';
import { SyncPreviewModal } from './SyncPreviewModal';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, CheckCircle2, XCircle, ArrowRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';

export const BulkReviewOrchestrator = ({ isOpen, onClose, onComplete, onTrademarkProcessed }) => {
    const { token } = useAuth();
    const [queue, setQueue] = useState([]); // [{id, expediente, denominacion}, ...]
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loadingQueue, setLoadingQueue] = useState(false);
    const [currentPreviewData, setCurrentPreviewData] = useState(null);
    const [loadingDiff, setLoadingDiff] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    // P2 UX: Stats tracking
    const [stats, setStats] = useState({ processed: 0, skipped: 0, errors: 0 });

    // 1. Cargar cola de pendientes al abrir
    useEffect(() => {
        if (isOpen) {
            loadQueue();
        } else {
            // Reset state on close
            setQueue([]);
            setCurrentIndex(0);
            setIsComplete(false);
            setCurrentPreviewData(null);
            setStats({ processed: 0, skipped: 0, errors: 0 });
        }
    }, [isOpen]);

    // 2. Cargar diff cuando cambia el índice o la cola
    useEffect(() => {
        if (queue.length > 0 && currentIndex < queue.length) {
            loadDiff(queue[currentIndex].id);
        } else if (queue.length > 0 && currentIndex > 0 && currentIndex >= queue.length) {
            setIsComplete(true);
        }
    }, [queue, currentIndex]);

    const loadQueue = async () => {
        setLoadingQueue(true);
        try {
            const res = await apiClient.get('/trademarks?has_pending_changes=true');
            // El endpoint devuelve detalle completo de marca
            setQueue(res.data || []);
            setCurrentIndex(0);
        } catch (error) {
            console.error("Error loading review queue:", error);
        } finally {
            setLoadingQueue(false);
        }
    };

    const loadDiff = async (trademarkId) => {
        setLoadingDiff(true);
        try {
            // 1. Intentar cargar changeset guardado (RÁPIDO)
            const res = await apiClient.get(`/trademarks/${trademarkId}/changesets/latest`);

            if (res.data && res.data.success && res.data.has_pending && res.data.changeset) {
                const cs = res.data.changeset;
                // ADAPTAR estructura para SyncPreviewModal
                const adaptedData = {
                    ...cs,
                    items_requires_approval: cs.items ? cs.items.filter(i => i.requires_approval) : [],
                    items_auto_apply: cs.items ? cs.items.filter(i => !i.requires_approval) : [],
                    summary: cs.summary_counts || { requires_approval: 0, added: 0, filled: 0 },
                    message: "Cambios pendientes de revisión"
                };
                setCurrentPreviewData(adaptedData);
                return true;
            }

            // 2. Fallback: Si no hay changeset guardado, forzar análisis en vivo (LENTO pero necesario)
            console.warn(`No pending changeset found for trademark ${trademarkId}, falling back to live sync.`);

            const previewRes = await apiClient.post(`/trademarks/${trademarkId}/sync/preview`, {});

            if (previewRes.data && previewRes.data.success) {
                // AUNQUE sean 0 cambios, mostramos el modal para que el usuario pueda confirmar y limpiar el flag
                console.log(`Live sync result: ${previewRes.data.total_changes} changes for ${trademarkId}`);
                setCurrentPreviewData(previewRes.data);
                return true;
            } else {
                console.error("Live sync failed:", previewRes.data);
                // Si falla el análisis, mostramos un estado de error en el modal en lugar de saltar silenciosamente
                setCurrentPreviewData({
                    id: 'error',
                    changeset_id: null,
                    items_requires_approval: [],
                    items_auto_apply: [],
                    summary: { requires_approval: 0, added: 0, filled: 0 },
                    message: "Error al analizar la marca. Intenta sincronizar manualmente.",
                    error: true
                });
                return false;
            }

        } catch (error) {
            console.error("Error loading diff:", error);
            // Si falla todo (red, CORS, 500), MOSTRAR ERROR en modal, NO SALTAR
            const isNetworkError = !error.response; // Sin respuesta del servidor = error de red
            const currentTm = queue[currentIndex];
            setCurrentPreviewData({
                id: 'error',
                changeset_id: null,
                items_requires_approval: [],
                items_auto_apply: [],
                summary: { requires_approval: 0, added: 0, filled: 0 },
                message: isNetworkError
                    ? `No se pudo conectar al servidor para analizar "${currentTm?.denominacion || currentTm?.expediente}". Verifica tu conexión o reintenta.`
                    : `Error al analizar "${currentTm?.denominacion || currentTm?.expediente}": ${error.response?.data?.detail || error.message || 'Error desconocido'}.`,
                errorType: isNetworkError ? 'network' : 'server',
                error: true
            });
            return false;
        } finally {
            setLoadingDiff(false);
        }
    };

    const handleApplySync = async (acceptedIds, rejectedIds) => {
        const trademarkId = queue[currentIndex]?.id;
        if (!trademarkId) return;

        try {
            await apiClient.post(`/trademarks/${trademarkId}/sync/apply`, {
                changeset_id: currentPreviewData.id || currentPreviewData.changeset_id,
                accepted_item_ids: acceptedIds,
                rejected_item_ids: rejectedIds
            });
            // Éxito
            if (onTrademarkProcessed) onTrademarkProcessed();
            setStats(prev => ({ ...prev, processed: prev.processed + 1 }));
            setCurrentIndex(prev => prev + 1);
        } catch (error) {
            console.error("Error applying sync:", error);
            const errorMsg = error.response?.data?.detail || error.message || "Error desconocido";
            // alert(`Error al aplicar cambios: ${errorMsg}. La marca se saltará.`); // UX improvement: no alert, just log and move on
            console.warn(`Skipping due to error: ${errorMsg}`);
            setStats(prev => ({ ...prev, errors: prev.errors + 1 }));
            setCurrentIndex(prev => prev + 1);
        }
    };

    // Render de carga inicial
    if (loadingQueue && isOpen) {
        return (
            <Dialog open={isOpen} onOpenChange={() => { }}>
                <DialogContent className="flex flex-col items-center py-12">
                    <Loader2 className="h-10 w-10 animate-spin text-indigo-600 mb-4" />
                    <p className="text-gray-500">Cargando actualizaciones pendientes...</p>
                </DialogContent>
            </Dialog>
        );
    }

    // Render vacío (no hay pendientes)
    if (queue.length === 0 && isOpen && !loadingQueue) {
        return (
            <Dialog open={isOpen} onOpenChange={onClose}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Todo al día</DialogTitle>
                        <DialogDescription>No hay marcas con cambios pendientes de revisión.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button onClick={onClose}>Cerrar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }

    if (isComplete) {
        return (
            <Dialog open={isOpen} onOpenChange={onClose}>
                <DialogContent>
                    <div className="flex flex-col items-center py-6 text-center w-full max-w-sm mx-auto">
                        <CheckCircle2 className="h-16 w-16 text-green-500 mb-4" />
                        <DialogTitle className="text-2xl mb-2">¡Revisión Completada!</DialogTitle>
                        <DialogDescription>
                            Has finalizado la revisión de marcas pendientes.
                        </DialogDescription>

                        <div className="grid grid-cols-3 gap-4 w-full mt-6">
                            <div className="bg-green-50 p-3 rounded-lg flex flex-col items-center">
                                <span className="text-2xl font-bold text-green-700">{stats.processed}</span>
                                <span className="text-xs text-green-600 font-medium">Aprobadas</span>
                            </div>
                            <div className="bg-gray-100 p-3 rounded-lg flex flex-col items-center">
                                <span className="text-2xl font-bold text-gray-700">{stats.skipped}</span>
                                <span className="text-xs text-gray-600 font-medium">Saltadas</span>
                            </div>
                            <div className="bg-red-50 p-3 rounded-lg flex flex-col items-center">
                                <span className="text-2xl font-bold text-red-700">{stats.errors}</span>
                                <span className="text-xs text-red-600 font-medium">Errores</span>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button onClick={() => { onClose(); if (onComplete) onComplete(); }} className="w-full sm:w-auto bg-indigo-600 text-white hover:bg-indigo-700">
                            Volver al Dashboard
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }

    const currentTrademark = queue[currentIndex];
    const progressLabel = `Marca ${currentIndex + 1} de ${queue.length}`;

    // Estado de error: mostrar un panel de error dedicado en lugar de SyncPreviewModal
    if (currentPreviewData?.error) {
        const isNetwork = currentPreviewData.errorType === 'network';
        return (
            <Dialog open={isOpen} onOpenChange={onClose}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <XCircle className="h-5 w-5" />
                            Error de Análisis
                        </DialogTitle>
                        <DialogDescription className="pt-1">
                            <span className="text-xs text-gray-400 font-mono">{progressLabel}</span>
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-3">
                        <p className="text-sm text-gray-700 mb-3">{currentPreviewData.message}</p>
                        {isNetwork && (
                            <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                                <p className="text-sm text-amber-800">
                                    Si ya hiciste una sincronización masiva anteriormente, los cambios guardados pueden seguir disponibles después de reconectarte.
                                </p>
                            </div>
                        )}
                    </div>
                    <DialogFooter className="flex flex-col sm:flex-row gap-2">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setStats(prev => ({ ...prev, skipped: prev.skipped + 1 }));
                                setCurrentIndex(prev => prev + 1);
                            }}
                        >
                            Saltar Marca <ArrowRight className="ml-1 h-4 w-4" />
                        </Button>
                        <Button variant="outline" onClick={onClose}>Cancelar</Button>
                        <Button
                            className="bg-indigo-600 hover:bg-indigo-700 text-white"
                            onClick={() => loadDiff(currentTrademark.id)}
                        >
                            Reintentar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }

    if (loadingDiff || !currentPreviewData) {
        // Mostrar Loading State de la marca
        return (
            <Dialog open={isOpen} onOpenChange={() => { }}> {/* Prevenir cierre accidental */}
                <DialogContent className="flex flex-col items-center py-12">
                    <Loader2 className="h-10 w-10 animate-spin text-indigo-600 mb-4" />
                    <p className="text-gray-500">Cargando cambios de <span className="font-semibold text-gray-900">{currentTrademark?.denominacion || 'la marca'}</span>...</p>
                    <p className="text-xs text-gray-400 mt-2">{progressLabel}</p>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <SyncPreviewModal
            isOpen={isOpen}
            onClose={onClose} // Si cierra manual, cierra todo el wizard
            previewData={currentPreviewData}
            onApply={handleApplySync}
            onSkip={() => {
                setStats(prev => ({ ...prev, skipped: prev.skipped + 1 }));
                setCurrentIndex(prev => prev + 1);
            }}
            title={
                <div className="flex flex-col items-start gap-1">
                    <span className="text-lg font-bold">{currentTrademark?.denominacion || currentTrademark?.expediente}</span>
                    <div className="flex gap-2 items-center text-xs font-normal text-gray-500">
                        <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-mono">{progressLabel}</span>
                        <span>Exp: {currentTrademark?.expediente}</span>
                    </div>
                </div>
            }
            confirmLabel={currentIndex < queue.length - 1 ? "Confirmar y Siguiente" : "Confirmar y Finalizar"}
        />
    );
};
